import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Nav from '../shared/Nav';
import Logo from '../shared/Logo';
import { diagnosticQuestions } from '../../data/questions';
import './Exam.css';

const EXAM_TIME_LIMIT = 20 * 60; // 20 minutos
const EXAM_PASS_THRESHOLD = 70;

// En producción las preguntas vienen de la API con mix de comunes + específicas
// Acá reutilizamos el banco de diagnóstico como demo
function getExamQuestions(role) {
  const pool = [...diagnosticQuestions[role]];
  // Shuffle
  for (let i = pool.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [pool[i], pool[j]] = [pool[j], pool[i]];
  }
  return pool; // 10 preguntas
}

export default function Exam() {
  const { accessCode } = useParams();
  const navigate = useNavigate();

  // TODO: Validar accessCode y obtener rol desde la API
  const role = 'limpieza';

  const [questions] = useState(() => getExamQuestions(role));
  const [currentQ, setCurrentQ] = useState(0);
  const [answers, setAnswers] = useState({});
  const [timeLeft, setTimeLeft] = useState(EXAM_TIME_LIMIT);
  const [finished, setFinished] = useState(false);

  // Timer
  useEffect(() => {
    if (finished) return;
    const interval = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          finishExam();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [finished]);

  const formatTime = (s) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  const selectAnswer = (optIdx) => {
    setAnswers((prev) => ({ ...prev, [currentQ]: optIdx }));
  };

  const nextQuestion = () => {
    if (currentQ >= questions.length - 1) {
      finishExam();
      return;
    }
    setCurrentQ((prev) => prev + 1);
  };

  const finishExam = useCallback(() => {
    if (finished) return;
    setFinished(true);

    let correct = 0;
    questions.forEach((q, i) => {
      if (answers[i] === q.correct) correct++;
    });

    const percentage = Math.round((correct / questions.length) * 100);
    const passed = percentage >= EXAM_PASS_THRESHOLD;

    // TODO: POST al backend con resultados
    // En producción esto iría a /api/recruitment/academy/{accessCode}/exam

    // Navegar al resultado con datos en state
    navigate(`/resultado/${accessCode}`, {
      state: { correct, total: questions.length, percentage, passed },
    });
  }, [finished, questions, answers, navigate, accessCode]);

  const timerPercent = (timeLeft / EXAM_TIME_LIMIT) * 100;
  const timerClass =
    timerPercent < 15 ? 'exam__timer--danger' :
    timerPercent < 40 ? 'exam__timer--warning' : '';

  return (
    <div className="exam">
      <nav className="exam__nav">
        <Logo size="sm" />
        <div className={`exam__timer ${timerClass}`}>
          ⏱ {formatTime(timeLeft)}
        </div>
      </nav>

      <div className="exam__body">
        <div className="exam__header-section">
          <h2 className="ik-section-title">Examen final</h2>
          <p style={{ fontSize: 13, color: 'var(--ik-gray-500)', marginTop: 4 }}>
            Sin acceso a los videos. Responde lo que aprendiste.
          </p>
        </div>

        {/* Progress dots */}
        <div className="exam__progress">
          <div className="exam__dots">
            {questions.map((_, i) => (
              <div
                key={i}
                className={`exam__dot ${
                  answers[i] !== undefined ? 'exam__dot--answered' :
                  i === currentQ ? 'exam__dot--current' : ''
                }`}
              />
            ))}
          </div>
          <span style={{ fontSize: 13, color: 'var(--ik-gray-500)' }}>
            {currentQ + 1}/{questions.length}
          </span>
        </div>

        {/* Question */}
        {questions[currentQ] && (
          <div className="appform__q-card ik-fade-in" key={currentQ}>
            <div className="appform__q-num ik-eyebrow">
              Pregunta {currentQ + 1} de {questions.length}
            </div>
            {questions[currentQ].context && (
              <div className="appform__q-context">{questions[currentQ].context}</div>
            )}
            <div className="appform__q-text">{questions[currentQ].question}</div>
            {questions[currentQ].image && (
              <div className="appform__q-photo">
                <img
                  src={questions[currentQ].image}
                  alt={`Situación: ${questions[currentQ].question}`}
                  loading="lazy"
                />
              </div>
            )}
            <div className="appform__q-options">
              {questions[currentQ].options.map((opt, j) => (
                <button
                  key={j}
                  className={`appform__q-opt ${answers[currentQ] === j ? 'appform__q-opt--selected' : ''}`}
                  onClick={() => selectAnswer(j)}
                >
                  <span className="appform__q-opt-letter">
                    {String.fromCharCode(65 + j)}
                  </span>
                  {opt}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="appform__q-nav" style={{ marginTop: 24 }}>
          <span />
          <button
            className="ik-btn ik-btn--primary"
            disabled={answers[currentQ] === undefined}
            onClick={nextQuestion}
          >
            {currentQ === questions.length - 1 ? 'Finalizar examen →' : 'Siguiente →'}
          </button>
        </div>
      </div>
    </div>
  );
}
